// ==============================================
// An APK set (base.apk plus its split_config files) as the App Library sees
// it: read every file, then sum up what the set says about the app, so the
// admin never types what the files already know.
// ==============================================
import { readApkInfo, sha256File, type ApkInfo } from './apkManifest';

export interface PickedApk { file: File; info: ApkInfo | null; sha256: string | null; error: string | null }

export interface ApkSetSummary {
  base: PickedApk | null;
  packageName: string | null;
  versionName: string;
  versionCode: number | null;
  label: string | null;
  labelGuessed: boolean;
  vendor: string | null;
  vendorSource: ApkInfo['vendorSource'];
  minSdk: number | null;
  targetSdk: number | null;
  totalBytes: number;
  /** One plain line saying why this set cannot be uploaded, or null. */
  problem: string | null;
}

/** The server keeps vendor names to 120 characters. */
const VENDOR_MAX = 120;

/** Play delivers base.apk + split_config.*.apk; keep split names, make the base predictable. */
export const uploadName = (p: PickedApk) => (p.info && !p.info.isSplit ? 'base.apk' : p.file.name);

export async function readApkSet(files: FileList | File[]): Promise<PickedApk[]> {
  const out: PickedApk[] = [];
  for (const file of Array.from(files)) {
    if (!file.name.toLowerCase().endsWith('.apk')) continue;
    try {
      const [info, sha256] = await Promise.all([readApkInfo(file), sha256File(file)]);
      out.push({ file, info, sha256, error: null });
    } catch (e) {
      out.push({ file, info: null, sha256: null, error: e instanceof Error ? e.message : 'Could not read this file' });
    }
  }
  return out;
}

export function summariseApkSet(picked: PickedApk[]): ApkSetSummary {
  const base = picked.find((p) => p.info && !p.info.isSplit) ?? null;
  const b = base?.info ?? null;
  const totalBytes = picked.reduce((n, p) => n + p.file.size, 0);
  const unreadable = picked.find((p) => !p.info || !p.sha256);
  const otherPkg = b ? picked.find((p) => p.info && p.info.packageName !== b.packageName) : null;
  const problem = picked.length === 0 ? null
    : unreadable ? `${unreadable.file.name} could not be read: ${unreadable.error ?? 'no manifest found'}.`
    : !b ? 'There is no base.apk in this set (the file without a split name). Add it with its splits.'
    : otherPkg ? `${otherPkg.file.name} belongs to ${otherPkg.info!.packageName}, not ${b.packageName}. Pick files from one app only.`
    : null;
  return {
    base,
    packageName: b?.packageName || null,
    versionName: b?.versionName ?? '',
    versionCode: b ? b.versionCode : null,
    label: b?.label ?? null,
    labelGuessed: b?.labelGuessed ?? false,
    vendor: b?.vendor ? b.vendor.slice(0, VENDOR_MAX) : null,
    vendorSource: b?.vendorSource ?? null,
    minSdk: b?.minSdk ?? null,
    targetSdk: b?.targetSdk ?? null,
    totalBytes,
    problem,
  };
}

export function findExistingApp<T extends { packageName: string }>(apps: T[], packageName: string | null): T | null {
  if (!packageName) return null;
  return apps.find((a) => a.packageName === packageName) ?? null;
}

/** Android release names for the API levels an admin is likely to meet. */
const ANDROID_RELEASE: Record<number, string> = {
  21: '5.0', 22: '5.1', 23: '6', 24: '7.0', 25: '7.1', 26: '8.0', 27: '8.1', 28: '9', 29: '10', 30: '11',
  31: '12', 32: '12L', 33: '13', 34: '14', 35: '15', 36: '16', 37: '17',
};
export const androidVersion = (api: number) => (ANDROID_RELEASE[api] ? `Android ${ANDROID_RELEASE[api]} (API ${api})` : `API ${api}`);
